// src/components/Donation/DonationTiers.js
import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { sanitizeHtml } from '../../utils/sanitizeHtml';
import './DonationTiers.css';

const TierIcon = ({ tier }) => {
  const kind = tier.type === 'merchandise'
    ? 'merchandise'
    : tier.frequency === 'monthly'
      ? 'membership'
      : tier.id === 'coffee'
        ? 'coffee'
        : 'support';

  const paths = {
    merchandise: <><path d="M4 7h16v13H4zM8 7V4h8v3M4 11h16" /></>,
    membership: <><path d="m12 3 2.7 5.5 6.1.9-4.4 4.3 1 6.1-5.4-2.9-5.4 2.9 1-6.1-4.4-4.3 6.1-.9Z" /></>,
    coffee: <><path d="M5 7h11v7a5 5 0 0 1-5 5H10a5 5 0 0 1-5-5V7ZM16 9h2a3 3 0 0 1 0 6h-2M8 3v2M12 3v2" /></>,
    support: <path d="M20.8 5.7a5.4 5.4 0 0 0-7.6 0L12 6.9l-1.2-1.2a5.4 5.4 0 1 0-7.6 7.6L12 22l8.8-8.7a5.4 5.4 0 0 0 0-7.6Z" />,
  };

  return <svg viewBox="0 0 24 24" aria-hidden="true">{paths[kind]}</svg>;
};

// Tier data keeps a color for API compatibility, but the UI uses the shared
// theme accent so backend-authored values cannot reintroduce retired branding.
const defaultTiers = [
  {
    id: 'stickers',
    name: "GUHSO Sticker Pack",
    price: 2.99,
    priceDisplay: "$2.99",
    frequency: "one-time",
    color: "#f7d135",
    popular: false,
    tagline: "Rep di brand everywhere",
    description: "Quality vinyl stickers featuring GUHSO logos and Jamaican-inspired designs",
    perks: [
      "5 premium vinyl stickers",
      "Weather-resistant material",
      "Perfect for laptops, phones, cars",
      "Free worldwide shipping"
    ],
    buttonText: "Get Mi Stickers",
    type: "merchandise",
    shippingRequired: true
  },
  {
    id: 'coffee',
    name: "Buy Mi A Coffee",
    price: 4.99,
    priceDisplay: "$4.99",
    frequency: "one-time",
    color: "#f7d135",
    popular: false,
    tagline: "Keep di vibes flowing",
    description: "Every coffee helps us stay up editing that next fire episode",
    perks: [
      "Shoutout in episode credits",
      "Access to blooper reel"
    ],
    buttonText: "Buy Mi Coffee Now",
    type: "donation"
  },
  {
    id: 'lunch',
    name: "Lunch Money",
    price: 9.99,
    priceDisplay: "$9.99",
    frequency: "one-time",
    color: "#f7d135",
    popular: true,
    tagline: "Fuel the hustle",
    description: "Help us grab a patty and cocoa bread while we work on exclusive content",
    perks: [
      "Everything from Coffee tier",
      "Early access to episodes (24 hours before public)",
      "Exclusive \"Big Up\" mention on air"
    ],
    buttonText: "Grab Mi Lunch",
    type: "donation"
  },
  {
    id: 'feed-team',
    name: "Feed Di Team",
    price: 19.99,
    priceDisplay: "$19.99",
    frequency: "one-time",
    color: "#F7D135",
    popular: false,
    tagline: "Breakfast & Lunch sorted!",
    description: "Keep the whole GUHSO crew energized for a full day of creating",
    perks: [
      "Everything from previous tiers",
      "Monthly exclusive mini-episode",
      "Access to private Discord/WhatsApp group",
      "Behind-the-scenes content"
    ],
    buttonText: "Feed Di Whole Crew",
    type: "donation"
  },
  {
    id: 'tshirt',
    name: "GUHSO Crew Neck Tee",
    price: 24.99,
    priceDisplay: "$24.99",
    frequency: "one-time",
    color: "#f7d135",
    popular: false,
    tagline: "Wear di culture with pride",
    description: "Premium black crew neck t-shirt with GUHSO logo in our signature color of the month",
    perks: [
      "100% cotton premium quality",
      "GUHSO logo in signature color",
      "Comfortable crew neck fit",
      "Available in S, M, L, XL",
      "Free shipping on orders over $20"
    ],
    buttonText: "Get Mi Tee",
    type: "merchandise",
    shippingRequired: true,
    hasOptions: true,
    options: {
      size: {
        label: "Size",
        required: true,
        choices: ["S", "M", "L", "XL"]
      }
    }
  },
  {
    id: 'insider',
    name: "GUHSO Insider",
    price: 3.99,
    priceDisplay: "$3.99",
    frequency: "monthly",
    color: "#f7d135",
    popular: false,
    badge: "BEST VALUE",
    tagline: "Join di inner circle",
    description: "Exclusive vibes, early drops, and direct access to the GUHSO family",
    perks: [
      "Ad-free episodes",
      "48-hour early access to all content",
      "Monthly Q&A sessions",
      "Exclusive merchandise discounts",
      "Vote on episode topics",
      "Access to full episode archive"
    ],
    buttonText: "Join Di Family",
    type: "subscription"
  }
];

const DonationTiers = ({ onAddToCart, incomingTiers = [] }) => {
  const [selectedTier, setSelectedTier] = useState(null);
  const [showOptionsModal, setShowOptionsModal] = useState(null);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [selectedQuantity, setSelectedQuantity] = useState(1);
  const { showSuccess } = useToast();
  const tiers = Array.isArray(incomingTiers) && incomingTiers.length > 0 ? incomingTiers : defaultTiers;

  const handleSelectTier = (tier) => {
    // If tier has options (like t-shirt sizes), show options modal
    if (tier.hasOptions) {
      setShowOptionsModal(tier);
      setSelectedOptions({});
      setSelectedQuantity(1);
      return;
    }

    // Otherwise add directly to cart
    addTierToCart(tier);
  };

  const addTierToCart = (tier, options = {}, quantity = 1) => {
    setSelectedTier(tier.id);
    
    const tierWithOptions = {
      ...tier,
      selectedOptions: options,
      quantity: quantity,
      // Add size to display name if selected
      displayName: options.size ? `${tier.name} (${options.size})` : tier.name
    };
    
    const result = onAddToCart ? onAddToCart(tierWithOptions) : null;
    
    // Show success toast
    const toastMessage = result?.destination === 'membership'
      ? 'Continue with the membership form to start subscriber access.'
      : `Added "${tierWithOptions.displayName}" to your shopping bag.`;
    
    showSuccess(toastMessage, 2500);
    
    // Reset selection visual feedback after a short delay
    setTimeout(() => setSelectedTier(null), 500);
  };

  const handleOptionChange = (optionName, value) => {
    setSelectedOptions(prev => ({
      ...prev,
      [optionName]: value
    }));
  };

  const handleAddWithOptions = () => {
    const tier = showOptionsModal;
    const isValid = validateOptions(tier, selectedOptions);
    
    if (!isValid) {
      showSuccess('Please select all required options', 2500);
      return;
    }

    addTierToCart(tier, selectedOptions, selectedQuantity);
    setShowOptionsModal(null);
    setSelectedOptions({});
    setSelectedQuantity(1);
  };

  const validateOptions = (tier, options) => {
    if (!tier.options) return true;
    
    for (const [optionName, optionConfig] of Object.entries(tier.options)) {
      if (optionConfig.required && !options[optionName]) {
        return false;
      }
    }
    return true;
  };

  return (
    <div className="donation-tiers-container">
      <div className="donation-header">
        <h2 className="donation-title">Keep Di Vibes Alive</h2>
        <p className="donation-subtitle">Every mickle mek a muckle - Support GUHSO</p>
      </div>

      {/* Donation Tiers Grid */}
      <div className="donation-grid">
        {tiers.map((tier) => (
          <div 
            key={tier.id}
            className={`donation-card ${tier.popular ? 'popular' : ''} ${selectedTier === tier.id ? 'selected' : ''}`}
            style={{
              '--tier-color': 'var(--color-accent)',
              '--tier-color-text': 'var(--color-accent-text)',
              '--tier-color-fill': 'var(--color-accent)'
            }}
          >
            {tier.popular && <span className="popular-badge">MOST POPULAR</span>}
            {tier.badge && !tier.popular && <span className="value-badge">{tier.badge}</span>}
            {tier.type === 'merchandise' && <span className="merch-badge">Merch</span>}
            
            <div className="donation-icon"><TierIcon tier={tier} /></div>
            {tier.image_url && (
              <div style={{ marginBottom: '0.8rem' }}>
                <img
                  src={tier.image_url}
                  alt={tier.name}
                  style={{ width: '100%', maxHeight: '170px', objectFit: 'cover', borderRadius: '10px' }}
                />
              </div>
            )}
            <h3 className="donation-name">{tier.name}</h3>
            <div className="donation-tagline">{tier.tagline}</div>
            
            <div className="donation-price">{tier.priceDisplay}</div>
            <div className="donation-frequency">
              {tier.frequency === 'monthly' ? '/month' : 'one-time'}
            </div>
            
            {tier.description_html ? (
              <div className="donation-description" dangerouslySetInnerHTML={{ __html: sanitizeHtml(tier.description_html) }} />
            ) : (
              <p className="donation-description">{tier.description}</p>
            )}
            
            <div className="donation-perks">
              {tier.perks.map((perk, index) => (
                <div key={index} className="perk-item">{perk}</div>
              ))}
            </div>
            
            <button 
              className={`donation-button ${tier.frequency === 'monthly' ? 'subscription-button' : ''}`}
              onClick={() => handleSelectTier(tier)}
            >
              {tier.buttonText}
            </button>

            {tier.id === 'insider' && (
              <div className="limited-offer">
                <span className="offer-badge">LIMITED TIME</span>
                <p>First 100 subscribers get exclusive GUHSO merch!</p>
                <p className="spots-left">Only 23 spots remaining</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Trust Indicators */}
      <div className="trust-section">
        <div className="trust-item">
          <i className="fas fa-shield-alt"></i>
          <span>Secure Payment</span>
        </div>
        <div className="trust-item">
          <i className="fas fa-lock"></i>
          <span>SSL Encrypted</span>
        </div>
        <div className="trust-item">
          <i className="fas fa-undo"></i>
          <span>Cancel Anytime</span>
        </div>
      </div>

      {/* Options Modal */}
      {showOptionsModal && (
        <div className="options-modal-overlay" onClick={() => setShowOptionsModal(null)}>
          <div className="options-modal" onClick={(e) => e.stopPropagation()}>
            <div className="options-header">
              <h3>
                <span className="options-title-icon"><TierIcon tier={showOptionsModal} /></span>
                {showOptionsModal.name}
              </h3>
              <button 
                className="options-close"
                onClick={() => setShowOptionsModal(null)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <div className="options-content">
              <p className="options-description">{showOptionsModal.description}</p>
              
              {showOptionsModal.options && Object.entries(showOptionsModal.options).map(([optionName, optionConfig]) => (
                <div key={optionName} className="option-group">
                  <label className="option-label">
                    {optionConfig.label}
                    {optionConfig.required && <span className="required">*</span>}
                  </label>
                  
                  {optionName === 'size' && (
                    <div className="size-selector">
                      {optionConfig.choices.map((size) => (
                        <button
                          key={size}
                          className={`size-btn ${selectedOptions.size === size ? 'selected' : ''}`}
                          onClick={() => handleOptionChange('size', size)}
                        >
                          {size}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Quantity Selector */}
              <div className="option-group">
                <label className="option-label">
                  Quantity
                  <span className="required">*</span>
                </label>
                <div className="quantity-selector">
                  <button
                    className="qty-btn"
                    onClick={() => setSelectedQuantity(Math.max(1, selectedQuantity - 1))}
                    disabled={selectedQuantity <= 1}
                  >
                    -
                  </button>
                  <span className="quantity-display">{selectedQuantity}</span>
                  <button
                    className="qty-btn"
                    onClick={() => setSelectedQuantity(selectedQuantity + 1)}
                    disabled={selectedQuantity >= 10}
                  >
                    +
                  </button>
                </div>
                <div className="quantity-note">
                  Maximum 10 items per order
                </div>
              </div>
              
              <div className="options-actions">
                <button 
                  className="cancel-btn"
                  onClick={() => setShowOptionsModal(null)}
                >
                  Cancel
                </button>
                <button 
                  className="add-to-cart-btn"
                  onClick={handleAddWithOptions}
                >
                  Add to Cart - ${(showOptionsModal.price * selectedQuantity).toFixed(2)}
                  {selectedQuantity > 1 && (
                    <span className="quantity-breakdown">
                      ({selectedQuantity} × {showOptionsModal.priceDisplay})
                    </span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DonationTiers;
